import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';

import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';

import { useTranslation } from 'react-i18next';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { GenericSelect, MultiIOSelect } from '../../FormWidgets/Select.js';
import MultiOutLabel, { MultiIOLabel } from '../../FormWidgets/MultiOutLabel';

import { ConnectionStatus } from '../../FormWidgets/SlaveWidgets.js';
import { SettingActions } from '../../components/index.js';
import { multiIOHwTypeMappping } from '../../Constants/constants';
import { makeMultiOutObject } from '../../Utils/utils.js';
import { settingControls } from '../../Tabs/settingFunctions';
import getStateManager from '../../StateManager.js';

const useStyles = makeStyles((theme) => ({
  slave: {
    display: 'block',
    border: '0.3em solid black',
    margin: '1em',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '1em',
    boxShadow: '0.5em 0.5em 2em 0.1em #00000'
  },

  label: {
    fontWeight: 'normal',
    color: theme.palette.primary.main
  },

  display: {
    display: 'block',
    padding: '1em',
    margin: '1em 0 1em 0',
    backgroundColor: '#1B1B1B',
    color: 'white'
  },

  lcd: {
    display: 'block',
    backgroundColor: 'blue',
    padding: '1em',
    fontFamily: 'Courier New',
    fontStyle: 'normal',
    border: '0.1em solid #B5B5FF'
  },

  sensors_out_ports: {
    display: 'block',
    padding: '1em',
    margin: '1em 0 1em 0'
  },

  block: {
    display: 'block'
  },

  connection: {
    height: '1.1em',
    width: '1.1em',
    borderRadius: '50%',
    display: 'inline-block',
    margin: '1em'
  }
}));

function getCommonZone(ports, zones) {
  if (ports.length === 0 || zones.length === 0) {
    return 'n/a';
  }

  let zoneSet = new Set(ports.map((p) => p.zone));
  if (zoneSet.size === 1) return [...zoneSet][0];
  return 'mixed';
}

function Slave({
  slave,
  zones,
  stateManager,
  controller,
  setConfirmDialog,
  update_expanded_slaves,
  multi_ios,
  setMultiIos,
  multi_io_controls,
  write_access
}) {
  const classes = useStyles();
  const [showOutport, setShowOutport] = useState(false);
  let { t } = useTranslation();

  let setSlaveZone = function (zone, ports = []) {
    if (controller.type === 6000) {
      let data = { slave: slave.address, zone: zone };
      if (ports.length > 0) data.ports = ports;
      stateManager.wsManager.sendWSMsg(controller, 'set_slave_zone', data, true);
    } else {
      const updatedSlaves = controller.slaves.map((s) =>
        s.address === slave.address ? { ...s, zone: parseInt(zone) } : s
      );
      stateManager.saveSettings(controller, controller, updatedSlaves, 'slaves');
    }
  };

  const saveSelectIndustrial = (slave, port_id, field, event) => {
    let value = event.target.value;
    let updated_ios = { ...multi_ios };
    updated_ios[slave][port_id][field] = value;
    setMultiIos(updated_ios);
    multi_io_controls.saveTempSettings(`multi_out_${slave}_${port_id}_${field}`, value);
  };

  let commonZone = slave.multi_outs ? getCommonZone(slave.multi_outs, zones) : null;
  return (
    <>
      <tr>
        <td>{slave.address.toString()}</td>
        <td>{slave.abilities.join(',')}</td>
        <td>
          <ConnectionStatus slave={slave} />
        </td>

        <td>
          {slave.abilities.includes('sensor') && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {slave.abilities.includes('out') && (
                <div style={{ marginRight: '1em' }}>Assign sensor ports to zone</div>
              )}
              <GenericSelect
                onChange={(evt) => setSlaveZone(evt.target.value)}
                values={Object.values(zones).map((z) => ({ value: z.id, label: z.name }))}
                value={slave.zone}
              />
            </div>
          )}

          {slave.abilities.includes('out') && (
            <>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {slave.abilities.includes('sensor') && (
                  <div style={{ marginRight: '1em' }}>Assign multi outs to zone</div>
                )}
                {controller.type === 5155 ? (
                  <GenericSelect
                    onChange={(evt) => {
                      setConfirmDialog({
                        open: true,
                        apply: () => setSlaveZone(evt.target.value),
                        close: () => setConfirmDialog({ open: false, apply: null, close: null })
                      });
                    }}
                    values={Object.values(zones).map((z) => ({ value: z.id, label: z.name }))}
                    value={slave.zone}
                  />
                ) : (
                  <GenericSelect
                    onChange={(evt) => {
                      setConfirmDialog({
                        open: true,
                        apply: () =>
                          setSlaveZone(
                            evt.target.value,
                            slave.multi_outs.map((mo) => mo.id)
                          ),
                        close: () => setConfirmDialog({ open: false, apply: null, close: null })
                      });
                    }}
                    values={Object.values(zones)
                      .map((z) => ({ value: z.id, label: z.name }))
                      .concat(commonZone === 'mixed' ? [{ value: 'mixed', label: 'mixed' }] : [])}
                    value={commonZone === 'mixed' ? 'mixed' : commonZone}
                  />
                )}
                <div
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                  onClick={() => {
                    setShowOutport(!showOutport);
                    update_expanded_slaves(slave.address, !showOutport);
                  }}
                >
                  {showOutport ? (
                    <>
                      <ExpandLessIcon /> Hide individual output ports
                    </>
                  ) : (
                    <>
                      <ExpandMoreIcon /> Show individual output ports
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </td>
      </tr>

      {showOutport && (
        <>
          {Object.keys(multi_ios).map(
            (slave_addr) =>
              slave_addr === slave.address.toString() &&
              Object.values(multi_ios[slave_addr]).map((io) => (
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>
                    Out {io.id + 1} {io.memo !== '' && `(${io.memo})`}
                  </td>
                  <td>
                    <label className={classes.label}>{t(multiIOHwTypeMappping[io.type_id])}</label>
                  </td>
                  <td>
                    <MultiIOSelect
                      parent={controller.zones[io.zone]}
                      controller={controller}
                      value={io.func ?? ''}
                      type={io.type_id ?? ''}
                      onChange={(event) => saveSelectIndustrial(slave_addr, io.id, 'func', event)}
                      disabled={!write_access}
                    />
                  </td>
                  <td>
                    <GenericSelect
                      onChange={(event) => saveSelectIndustrial(slave_addr, io.id, 'zone', event)}
                      values={Object.values(zones).map((z) => ({ value: z.id, label: z.name }))}
                      value={io.zone}
                    />
                  </td>
                </tr>
              ))
          )}
        </>
      )}
    </>
  );
}

export default function Slaves() {
  let { id } = useParams();
  let { t } = useTranslation();

  const stateManager = getStateManager();
  const controller = stateManager.getControllerById(id);

  const write_access =
    controller.access_rights.multi_out.write || controller.access_rights.multi_io.write;

  const [expanded_slaves, setExpandedSlaves] = useState(
    Object.fromEntries(controller.slaves.map((slave) => [slave.address, false]))
  );

  const update_expanded_slaves = function (id, expanded) {
    let slaves = { ...expanded_slaves };
    slaves[id] = expanded;
    setExpandedSlaves(slaves);
    console.log('expandedslaves', expanded_slaves);
  };

  const [confirmDialog, setConfirmDialog] = useState({ open: false, apply: null, close: null });

  const [multi_ios, setMultiIos] = useState(makeMultiOutObject(controller, undefined)); // use undefined to get ALL multi-ios
  const multi_io_controls = settingControls(
    controller,
    controller,
    'multi_io',
    multi_ios,
    setMultiIos
  );

  return (
    <>
      <ConfirmPortZoneDialog data={confirmDialog} />
      {Object.values(expanded_slaves).filter((s) => s).length > 0 && write_access && (
        <SettingActions
          onReset={multi_io_controls.resetSettings}
          onSave={() => multi_io_controls.saveSettings()}
        />
      )}
      <table style={{ width: '100%' }}>
        <thead style={{ fontWeight: 'bold', fontSize: '1.3em' }}>
          <td>Adress</td>
          <td>Abilities</td>
          <td>Connected</td>
          <td>Zone</td>
          {Object.values(expanded_slaves).filter((s) => s).length > 0 && (
            <>
              <td>{t('id')}</td>
              <td>{t('type')}</td>
              <td>{t('function')}</td>
            </>
          )}
        </thead>
        <tbody>
          {controller.slaves.map((slave) => (
            <Slave
              slave={slave}
              setConfirmDialog={setConfirmDialog}
              zones={controller.zones}
              stateManager={stateManager}
              controller={controller}
              update_expanded_slaves={update_expanded_slaves}
              multi_ios={multi_ios}
              setMultiIos={setMultiIos}
              multi_io_controls={multi_io_controls}
              write_access={write_access}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}

function ConfirmPortZoneDialog({ data }) {
  let { t } = useTranslation();

  return (
    <Dialog open={data.open} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">{t('confirm-port-zone')}</DialogTitle>
      <DialogContent>
        <Typography> {t('confirm-port-zone')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            data.apply();
            data.close();
          }}
          color="primary"
        >
          {t('auto_save_ok')}
        </Button>
        <Button onClick={() => data.close()} color="primary">
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
